import {
  calculateWardrobePrice,
  calculateRackPrice,
  calculatePrice,
  getConstraints,
  type FurnitureType,
} from '~/config/furnitureConstraints'
import {
  ColorName,
  colorHexCodes,
  getColorItemByName,
} from '~/utils/colorDictionary'
import { calculateTablePrice } from '~/components/ProductPage/productTypes/table'
import { calculateWorkTablePrice } from '~/components/ProductPage/productTypes/workTable'
import { calculateWardrobeColumnLayout } from '~/utils/wardrobeColumnLayout'
import { CDN_BASE } from '~/lib/cdn'
import { decodeWardrobeColumnConfigs } from '~/utils/wardrobeColumnConfigUrl'
import {
  WARDROBE_TEMPLATES,
  WardrobeTemplateId,
} from '~/config/wardrobeTemplates'
import { decodeColumnConfigs } from '~/utils/columnConfigUrl'
import { ColumnConfigurationType } from '~/types/columnConfigurationTypes'
import { findNearestAvailableConfiguration } from '~/utils/columnConfigurationFallback'

export type WidthRange = '0-99' | '100-159' | '160+'
export type CollectionType = 'standard' | 'new-collection'
export type StructureType = 'closed' | 'open' | 'semi-open'
export type CatalogProductType =
  | 'wardrobe'
  | 'stand'
  | 'tv-stand'
  | 'bedside'
  | 'rack'
  | 'table'
  | 'work-table'

export interface CatalogPreset {
  id: string
  imageId?: string
  type: CatalogProductType
  collection: CollectionType
  structureType: StructureType
  dimensions: { width: number; height: number; depth: number }
  colors: ColorName[]
  openingType?:
    | 'push'
    | 'round'
    | 'profile'
    | 'profile-long'
    | 'new-collection-handle'
  wardrobeCfg?: string
  rackCfg?: string
  colCfg?: string
  columns?: number
  plintHeight?: number
  drawers?: number
  isMirrored?: boolean
}

export { ColorName }

function getColorSlug(color: ColorName): string {
  const enumKey = Object.entries(ColorName).find(
    ([, val]) => val === color
  )?.[0]
  if (!enumKey) return color.replace(/\s+/g, '-').toLowerCase()
  return enumKey.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

export function getColorHex(color: ColorName): string {
  return colorHexCodes[color]
}

export function getCatalogImageUrl(
  type: CatalogProductType,
  id: string,
  color: ColorName,
  imageId?: string
): string {
  if (type === 'work-table') {
    const fileSlug = getColorItemByName(color)?.fileURL ?? 'natural-accacia'
    return `${CDN_BASE}/work-table/renders/large/${imageId ?? '1'}/${fileSlug}.jpg`
  }
  const slug = getColorSlug(color)
  return `${CDN_BASE}/catalog/${type}/${imageId ?? id}/${slug}.webp`
}

export function getWidthRange(width: number): WidthRange {
  if (width < 100) return '0-99'
  if (width < 160) return '100-159'
  return '160+'
}

function getWardrobeDoorCount(width: number): number {
  if (width <= 60) return 1
  if (width <= 100) return 2
  if (width <= 150) return width < 120 ? 2 : 3
  if (width < 200) return 3
  if (width === 200) return 5
  return 4
}

function getWardrobeTemplatesExtraCost(
  wardrobeCfg: string | undefined,
  columnCount: number
): number {
  const defaultTemplate =
    WARDROBE_TEMPLATES[WardrobeTemplateId.FULL_HANGING_WITH_1_SHELF]
  const defaultExtraCost = defaultTemplate?.extraCost ?? 0

  if (!wardrobeCfg) {
    return defaultExtraCost * columnCount
  }

  const decoded = decodeWardrobeColumnConfigs(wardrobeCfg)
  let total = 0
  for (let i = 0; i < columnCount; i++) {
    const cfg = decoded[i]
    if (cfg) {
      const template = WARDROBE_TEMPLATES[cfg.templateId]
      total += template?.extraCost ?? defaultExtraCost
    } else {
      total += defaultExtraCost
    }
  }
  return total
}

const DEFAULT_COLUMN_TYPE: Record<string, ColumnConfigurationType> = {
  stand: ColumnConfigurationType.DRAWERS_3,
  'tv-stand': ColumnConfigurationType.DRAWERS_2,
  bedside: ColumnConfigurationType.DRAWERS_2,
}

export function getColumnConfigTypes(
  preset: CatalogPreset
): ColumnConfigurationType[] {
  const { type, dimensions, colCfg } = preset
  const columns = preset.columns ?? 1
  const plintHeight =
    preset.plintHeight ??
    getConstraints(type as FurnitureType).dimensions.plintHeight.default

  const colDims = {
    width: dimensions.width / columns,
    height: dimensions.height - plintHeight,
    depth: dimensions.depth,
  }

  let types: ColumnConfigurationType[]
  if (colCfg) {
    const decoded = decodeColumnConfigs(colCfg)
    types = Array.from(
      { length: columns },
      (_, i) =>
        decoded[i]?.type ??
        DEFAULT_COLUMN_TYPE[type] ??
        ColumnConfigurationType.DRAWERS_3
    )
  } else {
    const defaultType =
      DEFAULT_COLUMN_TYPE[type] ?? ColumnConfigurationType.DRAWERS_3
    types = Array(columns).fill(defaultType)
  }

  const productType = type as 'stand' | 'tv-stand' | 'bedside'
  return types.map(
    (t) => findNearestAvailableConfiguration(t, colDims, productType) ?? t
  )
}

const round10Table = (n: number): number => Math.round(n / 10) * 10

export function getCatalogItemPrice(preset: CatalogPreset): number {
  const { type, dimensions, columns = 1, openingType } = preset

  if (type === 'table') {
    const texture = preset.colors[0] as string
    return round10Table(
      calculateTablePrice(texture, dimensions.depth, dimensions.width) * 0.7
    )
  }

  if (type === 'work-table') {
    const hasDrawers = (preset.drawers ?? 0) > 0
    return calculateWorkTablePrice(
      dimensions.width,
      dimensions.height,
      hasDrawers
    )
  }

  if (type === 'wardrobe') {
    const isNewCollection = preset.collection === 'new-collection'
    const layout = calculateWardrobeColumnLayout(
      dimensions.width,
      preset.isMirrored ?? false,
      isNewCollection
    )
    const doors = getWardrobeDoorCount(dimensions.width)
    const templatesExtraCost = getWardrobeTemplatesExtraCost(
      preset.wardrobeCfg,
      layout.columnCount
    )

    return calculateWardrobePrice({
      width: dimensions.width,
      height: dimensions.height,
      depth: dimensions.depth,
      columns: layout.columnCount,
      doors,
      sectionsPrice: 0,
      templatesExtraCost,
    })
  }

  if (type === 'rack') {
    return calculateRackPrice({
      width: dimensions.width,
      height: dimensions.height,
      depth: dimensions.depth,
      columns,
      templatesExtraCost: 0,
    })
  }

  const isPush = !openingType || openingType === 'push'

  const columnConfigTypes = getColumnConfigTypes(preset)

  return calculatePrice(
    type,
    dimensions,
    columns,
    preset.drawers ?? 3,
    columnConfigTypes,
    isPush
  )
}

const C = ColorName

export const catalogPresets: CatalogPreset[] = [
  // ── Wardrobes: new collection ──
  {
    id: 'CAT-WR-NC-001',
    type: 'wardrobe',
    collection: 'new-collection',
    structureType: 'closed',
    dimensions: { width: 100, height: 240, depth: 60 },
    colors: [C.GarnetRed, C.BlackBrown, C.GreenSalvia, C.CaramelNude],
    openingType: 'new-collection-handle',
    columns: 2,
  },
  {
    id: 'CAT-WR-012',
    type: 'wardrobe',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 90, height: 250, depth: 60 },
    colors: [C.White, C.LightGrey, C.BeigeSand, C.DarkGrey],
    openingType: 'profile-long',
    columns: 2,
  },
  {
    id: 'CAT-WR-NC-003',
    type: 'wardrobe',
    collection: 'new-collection',
    structureType: 'closed',
    dimensions: { width: 200, height: 270, depth: 45 },
    colors: [C.DenimBlue, C.GreenFjord, C.GreyCubanit, C.GreenSalvia],
    openingType: 'new-collection-handle',
    columns: 2,
  },
  {
    id: 'CAT-WR-002',
    type: 'wardrobe',
    collection: 'standard',
    structureType: 'semi-open',
    dimensions: { width: 150, height: 265, depth: 50 },
    colors: [C.RoseAntique, C.White, C.BeigeAlmond, C.BeigeSand],
    openingType: 'profile',
    columns: 2,
    wardrobeCfg: 'F1S,SO:0',
  },

  {
    id: 'CAT-WR-004',
    type: 'wardrobe',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 270, height: 250, depth: 60 },
    colors: [C.GreyStone, C.Grey, C.LightGrey, C.DarkGrey],
    openingType: 'round',
  },
  {
    id: 'CAT-WR-NC-005-A',
    imageId: 'CAT-WR-NC-005',
    type: 'wardrobe',
    collection: 'new-collection',
    structureType: 'closed',
    dimensions: { width: 150, height: 250, depth: 55 },
    colors: [C.BlackBrown, C.Grey, C.DenimBlue, C.CaramelNude],
    openingType: 'new-collection-handle',
    columns: 2,
  },
  {
    id: 'CAT-WR-001',
    type: 'wardrobe',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 150, height: 240, depth: 50 },
    colors: [C.BeigeAlmond, C.BeigeCashmere, C.GreenSalvia, C.GreyStone],
    columns: 2,
  },
  {
    id: 'CAT-WR-NC-005-B',
    imageId: 'CAT-WR-NC-005',
    type: 'wardrobe',
    collection: 'new-collection',
    structureType: 'closed',
    dimensions: { width: 150, height: 250, depth: 55 },
    colors: [C.BrownOak, C.BeigeAlmond, C.GarnetRed, C.GreyStone],
    openingType: 'new-collection-handle',
    columns: 2,
  },
  {
    id: 'CAT-WR-012-B',
    imageId: 'CAT-WR-012',
    type: 'wardrobe',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 90, height: 250, depth: 60 },
    colors: [C.Grey, C.GreyStone, C.RoseAntique, C.GreenSalvia],
    openingType: 'profile-long',
    columns: 2,
  },
  {
    id: 'CAT-WR-NC-004',
    type: 'wardrobe',
    collection: 'new-collection',
    structureType: 'closed',
    dimensions: { width: 60, height: 250, depth: 40 },
    colors: [C.GreenSalvia, C.GreyStone, C.NaturalAccacia, C.BrownOak],
    openingType: 'new-collection-handle',
    columns: 2,
  },
  {
    id: 'CAT-WR-011',
    type: 'wardrobe',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 90, height: 250, depth: 60 },
    colors: [C.Beige, C.LightGrey, C.BeigeCashmere, C.BeigeAlmond],
    openingType: 'profile',
    columns: 2,
  },

  // ── Stands (Comodă) ──
  {
    id: 'CAT-ST-005',
    type: 'stand',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 150, height: 90, depth: 40 },
    colors: [C.GreenSalvia, C.DarkGrey, C.RoseAntique, C.White],
    openingType: 'round',
    columns: 2,
    colCfg: 'DR4,DRDS3S',
  },

  {
    id: 'CAT-ST-007',
    type: 'stand',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 90, height: 110, depth: 40 },
    colors: [C.DarkGrey, C.BeigeCashmere, C.White, C.BeigeSand],
    columns: 1,
    colCfg: 'DR5',
  },
  {
    id: 'CAT-ST-008',
    type: 'stand',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 160, height: 120, depth: 40 },
    colors: [C.Grey, C.BeigeAlmond, C.White, C.GreyCubanit],
    openingType: 'profile',
    columns: 2,
    colCfg: 'DR5,DRDS4S',
  },
  {
    id: 'CAT-ST-006',
    type: 'stand',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 240, height: 100, depth: 40 },
    colors: [C.Beige, C.LightGrey, C.GreyCubanit, C.BeigeAlmond],
    openingType: 'profile',
    columns: 3,
    colCfg: 'DR4,DS2S,DRDS3S',
  },

  // // ── TV Stands (Comodă TV) ──
  {
    id: 'CAT-TV-002',
    type: 'tv-stand',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 270, height: 30, depth: 35 },
    colors: [C.BrownOak, C.BeigeAlmond, C.BeigeSand, C.GreenSalvia],
    openingType: 'push',
    columns: 4,
  },
  {
    id: 'CAT-TV-003',
    type: 'tv-stand',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 120, height: 45, depth: 45 },
    colors: [C.GreyStone, C.DarkGrey, C.BeigeCashmere, C.White],
    openingType: 'profile',
    columns: 2,
  },

  {
    id: 'CAT-TV-001',
    type: 'tv-stand',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 180, height: 45, depth: 40 },
    colors: [C.Grey, C.BeigeAlmond, C.White],
    openingType: 'push',
    columns: 2,
  },
  {
    id: 'CAT-TV-004',
    type: 'tv-stand',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 215, height: 30, depth: 35 },
    colors: [C.NaturalAccacia, C.LightGrey, C.GreenSalvia, C.DarkGrey],
    openingType: 'round',
    columns: 3,
  },
  // // ── Bedside (Noptieră) ──
  {
    id: 'CAT-BS-001',
    type: 'bedside',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 50, height: 50, depth: 40 },
    colors: [C.White, C.BeigeSand, C.LightGrey, C.DarkGrey],
    openingType: 'push',
    columns: 1,
  },

  {
    id: 'CAT-BS-003',
    type: 'bedside',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 70, height: 55, depth: 50 },
    colors: [C.DarkGrey],
    // colors: [C.DarkGrey, C.White, C.BeigeCashmere, C.BeigeAlmond],
    openingType: 'round',
    columns: 1,
  },
  {
    id: 'CAT-BS-002',
    type: 'bedside',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 70, height: 30, depth: 35 },
    colors: [C.GreenSalvia, C.BeigeAlmond, C.GreyCubanit, C.RoseAntique],
    openingType: 'profile',
    columns: 1,
  },
  // {
  //   id: 'CAT-BS-004',
  //   type: 'bedside',
  //   collection: 'standard',
  //   structureType: 'closed',
  //   dimensions: { width: 70, height: 30, depth: 50 },
  //   colors: [C.BeigeCashmere, C.White, C.DarkGrey, C.RoseAntique],
  //   openingType: 'push',
  //   columns: 1,
  // },

  // // ── Racks (Etajeră) ──
  // {
  //   id: 'CAT-RK-001',
  //   type: 'rack',
  //   collection: 'standard',
  //   structureType: 'open',
  //   dimensions: { width: 80, height: 200, depth: 35 },
  //   colors: [C.White, C.BeigeCashmere, C.DarkGrey, C.BeigeAlmond],
  //   openingType: 'push',
  //   columns: 2,
  // },
  // {
  //   id: 'CAT-RK-002',
  //   type: 'rack',
  //   collection: 'standard',
  //   structureType: 'open',
  //   dimensions: { width: 120, height: 200, depth: 35 },
  //   colors: [C.BeigeCashmere, C.White, C.GreyCubanit, C.DarkGrey],
  //   openingType: 'push',
  //   columns: 3,
  // },
  // {
  //   id: 'CAT-RK-003',
  //   type: 'rack',
  //   collection: 'standard',
  //   structureType: 'semi-open',
  //   dimensions: { width: 160, height: 200, depth: 35 },
  //   colors: [C.DarkGrey, C.White, C.BeigeCashmere, C.BeigeAlmond],
  //   openingType: 'push',
  //   columns: 4,
  // },
  // {
  //   id: 'CAT-RK-004',
  //   type: 'rack',
  //   collection: 'standard',
  //   structureType: 'open',
  //   dimensions: { width: 60, height: 200, depth: 35 },
  //   colors: [C.GreyCubanit, C.White, C.BeigeCashmere, C.DarkGrey],
  //   openingType: 'push',
  //   columns: 1,
  // },

  // ── Table (Masă) ──
  {
    id: 'CAT-TBL-001',
    type: 'table',
    collection: 'standard',
    structureType: 'closed',
    dimensions: { width: 210, height: 75, depth: 100 },
    colors: [
      C.LightCasellaOak,
      // C.NaturalCasellaOak,
      C.BrownCasellaOak,
      C.MaroneCasellaOak,
    ],
  },

  // ── Work Table (Masă de lucru) ──
  {
    id: 'CAT-WT-001',
    imageId: '1',
    type: 'work-table',
    collection: 'new-collection',
    structureType: 'closed',
    dimensions: { width: 100, height: 75, depth: 60 },
    // GreenSalvia (U638) and CaramelNude (U830) are not in the work-table
    // backend palette, so they price with no chipboard (wrong "de la"). Kept
    // only colours that map to a real chipboard.
    colors: [C.BlackBrown, C.GarnetRed],
  },
  {
    id: 'CAT-WT-002',
    imageId: '3',
    type: 'work-table',
    collection: 'new-collection',
    structureType: 'closed',
    dimensions: { width: 160, height: 75, depth: 60 },
    // NaturalAccacia (H3840) is not in the work-table backend palette.
    colors: [C.DenimBlue, C.BrownOak, C.GreyCubanit],
    // drawers: 3,
  },
  {
    id: 'CAT-WT-003',
    imageId: '2',
    type: 'work-table',
    collection: 'new-collection',
    structureType: 'closed',
    dimensions: { width: 120, height: 75, depth: 60 },
    colors: [C.BeigeAlmond, C.GreenFjord, C.DarkGrey, C.GreyStone],
    // drawers: 2,
  },
]
